import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DefaultTemplate } from 'views/templates/DefaultTemplate';
import {
  useSelector as useReduxSelector,
  TypedUseSelectorHook,
} from 'react-redux';
import { useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';
import { localeIds } from 'locals';
import { AppState, useAppDispatch } from 'stores/type';
import { DistributionDomain } from 'models/Distribution';
import { Comment, Distribution } from 'models/Distribution/type';
import { Application } from 'models/Application/type';
import { setErrorMessageLocaleId, setExceptionError } from 'stores/errorSlice';
import { showDialog, closeDialog } from 'stores/dialogSlice';
import { Splash } from 'views/components/molecules/Splash';
import { CommentsPageLayout } from 'views/layouts/CommentsPageLayout';

interface CommentsPageParams {
  distributionAccessKey: string;
}

export const CommentsPage: React.FC = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const useSelector: TypedUseSelectorHook<AppState> = useReduxSelector;
  const device = useSelector((state) => state.deviceState.device);
  const { distributionAccessKey } = useParams<CommentsPageParams>();
  const distributionDomain = useMemo(() => {
    return new DistributionDomain(distributionAccessKey, device);
  }, [device, distributionAccessKey]);
  const intl = useIntl();
  const [application, setApplication] = useState<Application | undefined>(
    undefined
  );
  const [distribution, setDistribution] = useState<Distribution | undefined>(
    undefined
  );
  const [commentList, setCommentList] = useState<Comment[] | undefined>(
    undefined
  );
  const [nextPage, setNextPage] = useState<number | undefined>(undefined);

  const getComments = useCallback(
    (refresh?: boolean) => {
      const page = refresh ? undefined : nextPage;

      distributionDomain
        .commentList(page)
        .then((result) => {
          if (result.isFailure()) {
            dispatch(
              showDialog({
                content: intl.formatMessage({ id: result.value.localeId() }),
              })
            );
            return;
          }

          setCommentList((state) => {
            if (state === undefined || refresh) {
              return result.value.list;
            }

            return state.concat(result.value.list);
          });
          if (result.value.page.current !== result.value.page.total) {
            setNextPage(result.value.page.current + 1);
          } else {
            setNextPage(undefined);
          }
        })
        .catch((e) => {
          dispatch(setExceptionError(e));
        });
    },
    [dispatch, distributionDomain, intl, nextPage]
  );

  useEffect(() => {
    getComments();
  }, [distributionAccessKey, getComments]);

  const handleCloseDialog = useCallback(() => {
    dispatch(closeDialog());
  }, [dispatch]);

  const handleCloseDialogAndGoBack = useCallback(() => {
    dispatch(closeDialog());
    history.goBack();
  }, [history, dispatch]);

  useEffect(() => {
    distributionDomain
      .detail()
      .then((result) => {
        if (result.isFailure()) {
          dispatch(
            showDialog({
              content: intl.formatMessage({ id: result.value.localeId() }),
              defaultAction: {
                label: intl.formatMessage({ id: localeIds.dialog.ok }),
                callback: handleCloseDialogAndGoBack,
              },
            })
          );
          return;
        }

        const [application, distribution] = result.value;
        setApplication(application);
        setDistribution(distribution);
      })
      .catch((e) => {
        dispatch(setExceptionError(e));
      });
  }, [
    dispatch,
    distributionAccessKey,
    distributionDomain,
    handleCloseDialogAndGoBack,
    intl,
  ]);

  const handlePostComment = (message: string, replyTo?: number) => {
    distributionDomain
      .postComment(message, replyTo)
      .then((result) => {
        if (result.isFailure()) {
          dispatch(setErrorMessageLocaleId(result.value.localeId()));
          return;
        }

        getComments(true);
      })
      .catch((e) => {
        dispatch(setExceptionError(e));
      });
  };

  const onDelete = useCallback(
    (id: number) => {
      distributionDomain
        .deleteComment(id)
        .then((result) => {
          if (result.isFailure()) {
            dispatch(setErrorMessageLocaleId(result.value.localeId()));
            return;
          }

          setCommentList((list) =>
            list?.filter((comment) => comment.id !== id)
          );
          getComments(true);
        })
        .catch((e) => {
          dispatch(setExceptionError(e));
        });
    },
    [distributionDomain, getComments, dispatch]
  );

  const handleDelete = (id: number) => {
    dispatch(
      showDialog({
        content: intl.formatMessage({
          id: localeIds.commentsPage.confirm.delete,
        }),
        cancelAction: {
          label: intl.formatMessage({ id: localeIds.dialog.cancel }),
          callback: handleCloseDialog,
        },
        defaultAction: {
          label: intl.formatMessage({ id: localeIds.dialog.ok }),
          callback: () => {
            onDelete(id);
            handleCloseDialog();
          },
        },
      })
    );
  };

  const [openModal, setOpenModal] = useState(false);
  const handleModalToOpen = useCallback(() => {
    setOpenModal(true);
  }, [setOpenModal]);
  const handleModalToClose = useCallback(() => {
    setOpenModal(false);
  }, [setOpenModal]);

  return commentList && application && distribution ? (
    <DefaultTemplate
      headerText={intl.formatMessage({
        id: localeIds.commentsPageLayout.title,
      })}
      onComment={handleModalToOpen}
      onBack={() => {
        history.goBack();
      }}
    >
      <CommentsPageLayout
        commentList={commentList}
        application={{
          name: application.name,
          ownerName: application.ownerName,
          iconSrc: application.iconSrc,
        }}
        author={{
          iconSrc: device?.user?.profileIcon || distribution.iconSrc,
          name: device?.user?.name || distribution.displayName,
        }}
        openModal={openModal}
        hasNextComment={nextPage !== undefined}
        onPostComment={handlePostComment}
        onDelete={handleDelete}
        onOpenModal={handleModalToOpen}
        onCloseModal={handleModalToClose}
        getNextComment={getComments}
      />
    </DefaultTemplate>
  ) : (
    <DefaultTemplate headerText="">
      <Splash />
    </DefaultTemplate>
  );
};
